import { createSelector } from '@reduxjs/toolkit';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import type { AppState } from '../../../providers/AppStateProvider/types';
import { getManualRFQPricingFormHandler } from './activeForm';
import { getRFQPricingFormSelectors } from './RFQPricingForm/RFQPricingFormState';

/** Returns selectors scoped to the provided instanceID */
export function getRFQPricingSelectors(instanceID: string) {
  const selectState = (state: AppState) => state.manualRFQPricing[instanceID];
  const selectIsDestroyed = (state: AppState) => state.manualRFQPricing[instanceID] == null;
  const formSelectors = getRFQPricingFormSelectors(instanceID);
  const selectSubmitError = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].activeFormKey,
    (state: AppState) => state,
    (activeFormKey, state) => getManualRFQPricingFormHandler(activeFormKey).getSubmitErrorSelector(instanceID)(state)
  );

  const selectCanRejectQuote = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].isLoading,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote,
    (isLoading, currentCustomerQuote): { canRejectQuote: boolean; text?: string } => {
      if (currentCustomerQuote == null) {
        return { canRejectQuote: false, text: 'Unable to find quote' };
      }

      if (isLoading) {
        // No text returned here - the button will be spinning which is enough
        return { canRejectQuote: false, text: undefined };
      }
      if (!currentCustomerQuote.isOpen) {
        return { canRejectQuote: false, text: `Quote is not open (${currentCustomerQuote.QuoteStatus})` };
      }
      return { canRejectQuote: true };
    }
  );

  const selectCanSendQuote = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].isLoading,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote,
    selectSubmitError,
    (isLoading, currentCustomerQuote, submitError): { canSendQuote: boolean; text?: string } => {
      if (currentCustomerQuote == null) {
        return { canSendQuote: false, text: 'Unable to find quote' };
      }

      if (isLoading) {
        // No text returned here - the button will be spinning which is enough
        return { canSendQuote: false, text: undefined };
      }
      if (!currentCustomerQuote.isOpen) {
        return { canSendQuote: false, text: `Quote is not open (${currentCustomerQuote.QuoteStatus})` };
      }
      if (submitError) {
        return { canSendQuote: false, text: submitError };
      }

      return { canSendQuote: true };
    }
  );

  const selectCustomerQuoteRequestParams = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuoteRFQID,
    rfqID => {
      return rfqID ? { RFQID: rfqID } : undefined;
    }
  );

  const selectCustomerQuote = createSelector(
    selectCustomerQuoteRequestParams,
    (state: AppState) => state,
    (query, state) => {
      if (!query) {
        return undefined;
      }
      return streamingDataSlice.endpoints.getCustomerQuotes.select(query)(state).data?.get(query.RFQID);
    }
  );

  return {
    selectState,
    selectIsDestroyed,
    selectCanRejectQuote,
    selectCanSendQuote,
    selectCustomerQuoteRequestParams,
    selectCustomerQuote,
    form: formSelectors,
  };
}
